<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { phoneNumber, channelType } = inbox.value;
  return getInboxIconByType(channelType, phoneNumber);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const lastClientMessageAt = computed(() => {
  const timestamp = props.conversation?.last_client_message_at;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const lastClientMessageActualTime = computed(() => {
  if (!props.conversation?.last_client_message_at) return '';
  const date = new Date(props.conversation.last_client_message_at * 1000);
  const now = new Date();

  // Start of local days to compute whole-day differences
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfMessageDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const diffDays = Math.floor((startOfToday - startOfMessageDay) / 86400000);

  const locale =
    (typeof navigator !== 'undefined' &&
      ((navigator.languages && navigator.languages[0]) ||
        navigator.language ||
        navigator.userLanguage)) ||
    'en-US';

  const timeStr = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Today: show only time (e.g. "10:35")
  if (diffDays === 0) return timeStr;

  // Within the last week (yesterday .. 6 days ago): show weekday + time (e.g. "Sun 10:35")
  if (diffDays > 0 && diffDays < 7) {
    const weekday = date.toLocaleDateString(locale, { weekday: 'short' });
    return `${weekday} ${timeStr}`;
  }

  // Older than a week: show month and day (e.g. "Aug 21")
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
});

const waitTimeInMinutes = computed(() => {
  if (!props.conversation?.last_client_message_at) return 0;

  // Check if the last message is from the client (incoming)
  // If the last activity is from an agent (outgoing), customer is not waiting
  const lastActivityTime = props.conversation.timestamp * 1000;
  const lastClientMessageTime =
    props.conversation.last_client_message_at * 1000;

  // If last activity is newer than last client message, customer is not waiting
  if (lastActivityTime > lastClientMessageTime) {
    return 0;
  }

  // Customer is waiting - calculate time since their last message
  const timeDiff = Date.now() - lastClientMessageTime;
  return Math.floor(timeDiff / 60000); // 60000 ms = 1 minute
});

const conversationColorClass = computed(() => {
  if (props.conversation.status === 'resolved') {
    return 'border-l-slate-600 bg-slate-100 dark:bg-slate-900/40';
  }
  const minutes = waitTimeInMinutes.value;
  if (minutes <= 15) return '';
  if (minutes <= 30)
    return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
  if (minutes <= 60) return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
  return 'border-l-violet-800 bg-violet-100 dark:bg-violet-900/40';
});

const colorClass = computed(() => {
  const minutes = waitTimeInMinutes.value;
  if (minutes <= 15) return '';
  if (minutes <= 30) return 'text-yellow-600 dark:text-yellow-400';
  if (minutes <= 60) return 'text-red-600 dark:text-red-400';
  return 'text-violet-800 dark:text-violet-900';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer border-l-2 border-transparent"
    :class="conversationColorClass"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <div class="flex flex-col items-end">
            <span class="text-sm text-n-slate-10">
              {{ lastActivityAt }} • {{ lastClientMessageAt }}
            </span>
            <span
              v-if="lastClientMessageActualTime"
              class="text-xs font-medium text-n-slate-10"
            >
              {{ lastClientMessageActualTime }}
            </span>
          </div>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
